// Everything the End of day hub and team roster need for one trading day:
// who worked (roster), who has checked in, and where the nightly numbers are.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { addDaysIso, localDateOf, parseCheckin, timeOf, type Checkin } from '@/src/lib/checkin';
import { DEFAULT_EOD_CONFIG, type EodCalc, type EodConfig, type EodData } from '@/src/lib/eod';

export interface NightCheckin {
  id: string;
  userId: string;
  name: string;
  role: string;
  position: string | null;
  submittedAt: string;
  updatedAt: string | null;
  answers: Record<string, unknown>;
  checkin: Checkin;
  /** Cached translations by language code (translate-checkins). */
  translations?: Record<string, { from: string | null; answers: Record<string, unknown> }>;
}
export interface NightPerson {
  userId: string;
  name: string;
  area: string | null;
  start: string; // "4:00pm"
  end: string;
  endIso: string;
  /** Has an OpsAlo login. People without one still show as working but can't send a check-in. */
  onOpsAlo?: boolean;
}
export interface NightNumbers {
  status: 'imported' | 'draft' | 'submitted';
  data: EodData | null;
  calc: EodCalc | null;
  total: number | null;
  covers: number | null;
  submittedBy: string | null;
  submittedAt: string | null;
  pdfPath: string | null;
  /** The venue's report setup (which steps it uses). */
  config?: EodConfig;
}

export function useEodNight(venueId: string, date: string, tz: string | null, canSeeNumbers: boolean) {
  const [checkins, setCheckins] = useState<NightCheckin[]>([]);
  const [people, setPeople] = useState<NightPerson[]>([]);
  const [numbers, setNumbers] = useState<NightNumbers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    // Check-ins for the day.
    const rep = await supabase.from('eod_reports').select('id, user_id, submitted_at, answers, translations, users(name, app_role, position)')
      .eq('venue_id', venueId).eq('report_date', date).order('submitted_at', { ascending: true });
    let repRows = rep.data as unknown[] | null;
    if (rep.error) {
      // Older schema without users.position.
      const r2 = await supabase.from('eod_reports').select('id, user_id, submitted_at, answers, users(name, app_role)')
        .eq('venue_id', venueId).eq('report_date', date).order('submitted_at', { ascending: true });
      if (r2.error) setError(r2.error.message);
      repRows = r2.data as unknown[] | null;
    }
    type RepRow = { id: string; user_id: string; submitted_at: string; updated_at?: string | null; answers: Record<string, unknown> | null; translations?: NightCheckin['translations'] | null; users: { name: string | null; app_role: string | null; position?: string | null } | Array<{ name: string | null; app_role: string | null; position?: string | null }> | null };
    const list = ((repRows ?? []) as RepRow[]).map((r) => {
      const u = Array.isArray(r.users) ? r.users[0] : r.users;
      return {
        id: r.id, userId: r.user_id, name: u?.name ?? 'Team member', role: u?.app_role ?? 'staff', position: u?.position ?? null,
        submittedAt: r.submitted_at, updatedAt: r.updated_at ?? null, answers: r.answers ?? {}, checkin: parseCheckin(r.answers), translations: r.translations ?? undefined,
      };
    });
    setCheckins(list);

    if (canSeeNumbers) {
      // Who worked: shifts that start on this day (venue time).
      const { data: shifts } = await supabase.from('shifts')
        .select('user_id, start_time, end_time, role_tag, status')
        .eq('venue_id', venueId).eq('published', true)
        .gte('start_time', `${addDaysIso(date, -1)}T00:00:00Z`).lte('start_time', `${addDaysIso(date, 1)}T23:59:59Z`);
      type S = { user_id: string | null; start_time: string; end_time: string; role_tag: string | null; status?: string | null };
      const mine = ((shifts ?? []) as S[]).filter((s) => s.user_id && localDateOf(s.start_time, tz) === date && String(s.status ?? '') !== 'cancelled')
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
      const ids = [...new Set(mine.map((s) => s.user_id as string))];
      const names = new Map<string, string>();
      const hasLogin = new Set<string>();
      if (ids.length) {
        let r = await supabase.from('users').select('id, name, auth_user_id').in('id', ids);
        if (r.error) r = await supabase.from('users').select('id, name').in('id', ids) as typeof r;
        for (const u of (r.data ?? []) as Array<{ id: string; name: string | null; auth_user_id?: string | null }>) {
          names.set(u.id, u.name ?? 'Team member');
          if (u.auth_user_id || !('auth_user_id' in u)) hasLogin.add(u.id);
        }
      }
      // One row per person: first start, last end, areas joined.
      const byUser = new Map<string, NightPerson & { areas: string[] }>();
      for (const s of mine) {
        const id = s.user_id as string;
        const cur = byUser.get(id);
        if (!cur) {
          byUser.set(id, { userId: id, name: names.get(id) ?? 'Team member', onOpsAlo: hasLogin.has(id), area: null, areas: s.role_tag ? [s.role_tag] : [], start: timeOf(s.start_time, tz), end: timeOf(s.end_time, tz), endIso: s.end_time });
        } else {
          if (s.end_time > cur.endIso) { cur.end = timeOf(s.end_time, tz); cur.endIso = s.end_time; }
          if (s.role_tag && !cur.areas.includes(s.role_tag)) cur.areas.push(s.role_tag);
        }
      }
      setPeople([...byUser.values()].map(({ areas, ...p }) => ({ ...p, area: areas.join(', ') || null })));

      // Nightly numbers for the day.
      let sd = await supabase.from('sales_days').select('status, eod, total_sales, covers, submitted_at, pdf_path, users:submitted_by(name)')
        .eq('venue_id', venueId).eq('trade_date', date).maybeSingle();
      if (sd.error) {
        sd = await supabase.from('sales_days').select('status, eod, total_sales, covers, submitted_at')
          .eq('venue_id', venueId).eq('trade_date', date).maybeSingle() as typeof sd;
      }
      const row = sd.data as null | { status: NightNumbers['status']; eod: (EodData & { calc?: EodCalc }) | null; total_sales: number | null; covers: number | null; submitted_at: string | null; pdf_path?: string | null; users?: { name: string | null } | null };
      const { data: cfgRow } = await supabase.from('eod_settings').select('config').eq('venue_id', venueId).maybeSingle();
      const config = { ...DEFAULT_EOD_CONFIG, ...((cfgRow?.config as Partial<EodConfig> | undefined) ?? {}) };
      if (row) {
        const hasEod = !!row.eod && Object.keys(row.eod).length > 0;
        const { calc, ...rest } = (row.eod ?? {}) as EodData & { calc?: EodCalc };
        setNumbers({
          status: row.status, data: hasEod ? (rest as EodData) : null, calc: calc ?? null,
          total: row.total_sales != null ? Number(row.total_sales) : null, covers: row.covers,
          submittedBy: row.users?.name ?? (rest as EodData).manager ?? null, submittedAt: row.submitted_at, pdfPath: row.pdf_path ?? null, config,
        });
      } else setNumbers(null);
    }
    setLoading(false);
  }, [venueId, date, tz, canSeeNumbers]);

  useEffect(() => { setLoading(true); void load(); }, [load]);

  // Live: a new or edited check-in shows up straight away.
  useEffect(() => {
    const ch = supabase.channel(`eod-night-${venueId}-${date}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'eod_reports', filter: `venue_id=eq.${venueId}` }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [venueId, date, load]);

  const remind = useCallback(async (userIds: string[]) => {
    const { data, error: err } = await supabase.rpc('remind_eod_checkin', { p_user_ids: userIds, p_date: date });
    if (err) return { error: /function|does not exist|schema cache/i.test(err.message) ? 'Reminders need the latest database update (20261023050000_eod_checkins.sql).' : err.message };
    return { sent: Number(data ?? 0) };
  }, [date]);

  return { checkins, people, numbers, loading, error, reload: load, remind };
}
